export enum DriverEventType {
  ACTION,
  INTERNAL_ERROR,
  NOTIFY,
  WARN,
  INFO,
}

export interface DriverNotifier {
  internalError(internalError: string): void;
  error(message: string): void;
  warning(message: string): void;
  info(message: string): void;
}

export interface DriverActionEffect<TAction> {
  type: DriverEventType.ACTION;
  action: TAction;
}

export interface DriverInternalErrorEffect {
  type: DriverEventType.INTERNAL_ERROR;
  internalError: string;
}

export interface DriverNotificationEffect {
  type: DriverEventType.NOTIFY;
  message: string;
}

export interface DriverWarningEffect {
  type: DriverEventType.WARN;
  message: string;
}

export interface DriverInformationEffect {
  type: DriverEventType.INFO;
  message: string;
}

export type DriverEffect<TAction> =
  | DriverActionEffect<TAction>
  | DriverInternalErrorEffect
  | DriverNotificationEffect
  | DriverWarningEffect
  | DriverInformationEffect;

/**
 * Processes a pre-drained snapshot of effects into Redux actions and immediate notifications.
 * Notifications are fired inline; actions are returned to the caller for dispatch.
 */
export function flushDriverEffects<TAction>(
  effects: ReadonlyArray<DriverEffect<TAction>>,
  notifier: DriverNotifier,
): TAction[] | undefined {
  if (effects.length === 0) {
    return undefined;
  }

  const actions: TAction[] = [];

  for (const effect of effects) {
    if (effect.type === DriverEventType.NOTIFY) {
      notifier.error(effect.message);
    } else if (effect.type === DriverEventType.INTERNAL_ERROR) {
      notifier.internalError(effect.internalError);
    } else if (effect.type === DriverEventType.WARN) {
      notifier.warning(effect.message);
    } else if (effect.type === DriverEventType.INFO) {
      notifier.info(effect.message);
    } else if (effect.type === DriverEventType.ACTION) {
      actions.push(effect.action);
    }
  }

  return actions.length > 0 ? actions : undefined;
}
